import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Card from '@components-clean/core/card';
import CoreCheckbox from '@components-clean/core/core-checkbox';
import Loading from '@components-clean/core/loading/loading';
import { TextArea } from '@components-clean/core/text-area';
import { CM2Dataset } from '@interfaces/central-monitoring';
import { stringDateWithoutTimeFromUtc } from '@utils/convertor';
import DatasetReviewsApi from '@apis/bff-api/central-monitoring/dataset-reviews';

interface IProps {
    studyRef: string;
    comparisonDataset?: CM2Dataset;
}

const ComparisonCard = ({ studyRef, comparisonDataset }: IProps) => {
    const { t } = useTranslation('cm', {
        keyPrefix: 'monitoringToolkit.studyDatasetReview.lastReviewCard',
    });
    interface IFormData {
        isReviewed: boolean;
        startDate: string;
        endDate: string;
        reviewerName: string;
        summary: string;
    }

    const EMPTY_FORM = {
        isReviewed: false,
        reviewerName: '-',
        startDate: '-',
        endDate: '-',
        summary: '',
    };

    const { data: datasetReview, isLoading } = DatasetReviewsApi.useGet(studyRef, comparisonDataset?.id);

    const [formData, setFormData] = useState<IFormData>(EMPTY_FORM);

    useEffect(() => {
        if (!datasetReview) {
            setFormData(EMPTY_FORM);
            return;
        }
        setFormData({
            isReviewed: datasetReview.reviewed ?? false,
            reviewerName: datasetReview.reviewer ?? '-',
            startDate: stringDateWithoutTimeFromUtc(datasetReview.startDate, 'DD-MMM-YYYY') ?? '-',
            endDate: stringDateWithoutTimeFromUtc(datasetReview.completedDate, 'DD-MMM-YYYY') ?? '-',
            summary: datasetReview.summary ?? '',
        });
    }, [datasetReview]);

    return (
        <Card
            className="card last-review-card"
            data-testid="last-review-card"
            title={`${t('title')}${!!comparisonDataset ? `: ${comparisonDataset.dataCutDate}` : ''}`}
        >
            <div className="top">
                <div className="checkbox-area">
                    <CoreCheckbox
                        data-testid="is-reviewed"
                        label={t('labels.reviewed')}
                        disabled
                        checked={formData.isReviewed}
                    />
                </div>
                <dl>
                    <dt>{t('labels.reviewStartDate')}</dt>
                    <dd>{formData.startDate}</dd>
                    <dt>{t('labels.reviewEndDate')}</dt>
                    <dd>{formData.endDate}</dd>
                    <dt>{t('labels.reviewer')}</dt>
                    <dd>{formData.reviewerName}</dd>
                </dl>
            </div>
            <div className="bottom">
                <TextArea
                    data-testid="review-summary"
                    label={t('labels.reviewSummary')}
                    readOnly
                    value={formData.summary}
                />
            </div>
            <div className={`loading-overlay ${!isLoading ? 'fade-out' : ''}`}>
                <Loading />
            </div>
        </Card>
    );
};

export default ComparisonCard;
